import React, { useState, useEffect, useRef } from "react";
import { scroller } from "react-scroll";
import { useLocation } from "react-router";
import { Column, Row } from "@/shared/ui/Blocks";
import { cn } from "@/shared/utils/shadcn";
import { Icon, IconName } from "@/shared/ui/Icon";
import useOrgNavigate from "@/shared/utils/router/useOrgNavigate";

export type TabLinkProps = {
  label: string;
  icon?: IconName;
  id: string;
  path?: string;
  isActive?: boolean;
};

interface TabSelectProps {
  tabs: TabLinkProps[];
  isInpage?: boolean;
  className?: string;
  scrollContainerId?: string;
  RightComponent?: (props: { activeTabId: string | null }) => React.ReactNode;
}

function TabNavigation(props: TabSelectProps) {
  const { tabs, isInpage, className, scrollContainerId, RightComponent } =
    props;
  const navigate = useOrgNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState<string | null>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  useEffect(() => {
    if (isInpage) {
      observerRef.current = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              setActiveTab(entry.target.id);
            }
          });
        },
        {
          root: scrollContainerId
            ? document.getElementById(scrollContainerId)
            : null,
          rootMargin: "-50% 0px -50% 0px", // Adjust this value to change when the section is considered "active"
          threshold: 0,
        }
      );

      tabs.forEach((tab) => {
        const element = document.getElementById(tab.id);
        if (element) {
          observerRef.current?.observe(element);
        }
      });

      return () => {
        if (observerRef.current) {
          observerRef.current.disconnect();
        }
      };
    }
  }, [isInpage, tabs, scrollContainerId]);

  const scrollToSection = (id: string) => {
    if (scrollContainerId) {
      return scroller.scrollTo(id, {
        duration: 500,
        smooth: true,
        containerId: scrollContainerId,
        offset: -64,
      });
    }
    scroller.scrollTo(id, {
      duration: 500,
      smooth: true,
      offset: -64,
    });
  };

  const getIsTabActive = (tab: TabLinkProps) => {
    if (isInpage) {
      return activeTab === tab.id;
    }
    return tab?.isActive || location.pathname === tab.path;
  };

  const activeClass = "bg-grey-100";

  const handleChange = (tab: TabLinkProps) => {
    if (isInpage) {
      scrollToSection(tab.id);
    } else {
      if (tab.path) {
        navigate(tab.path);
      }
    }
  };

  const TabLinks = tabs.map((tab) => {
    const isActive = getIsTabActive(tab);

    return (
      <Column
        key={tab.id}
        onClick={() => handleChange(tab)}
        className={cn("pb-2 text-grey-900 cursor-pointer select-none relative")}
      >
        <Row
          className={cn(
            "items-center px-4 py-1 rounded-lg",
            isActive && activeClass
          )}
        >
          {tab.icon && <Icon icon={tab.icon} size="sm" className="mr-2" />}
          <p
            className={cn(
              "text-sm hidden md:block",
              isActive && "font-semibold"
            )}
          >
            {tab.label}
          </p>
        </Row>
        {isActive && (
          <div className="absolute h-[2px] w-full bottom-[-11px] left-0 right-0 bg-blue-500 rounded-md" />
        )}
      </Column>
    );
  });

  return (
    <Row
      id="navigation-tabs"
      className={cn(
        "h-[64px] items-center space-x-4 border-b border-grey-300 border-solid mb-8 overflow-x-auto z-90",
        className
      )}
    >
      {TabLinks}
      {RightComponent && <RightComponent activeTabId={activeTab} />}
    </Row>
  );
}

export { TabNavigation };
